package diana.soleil.design.iraniancanadienbazzar.feature.profile

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.withStyle
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButtonVariant
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIcon
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIconButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.appContentWidth
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.terms_accept
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.terms_back_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.terms_body
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.terms_consent_hint
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.terms_decline
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.terms_decline_notice
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.terms_intro
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.terms_summary_body
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.terms_summary_disclaimer
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.terms_summary_title
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.terms_title
import diana.soleil.design.iraniancanadienbazzar.feature.profile.resources.terms_updated
import org.jetbrains.compose.resources.stringResource

/**
 * Terms of Service + liability disclaimer. Two modes:
 *  - Reading mode (Profile → Terms & Legal): back button, scrollable text.
 *  - CONSENT mode ([requireAcceptance] = true, shown by the app-entry gate until the current
 *    [diana.soleil.design.iraniancanadienbazzar.core.common.TermsPolicy] version is accepted):
 *    no back affordance; Agree/Disagree bar pinned at the bottom. Agreeing records version +
 *    timestamp (the consent record); disagreeing shows a persistent notice — the app is not
 *    usable without acceptance.
 */
@Composable
fun TermsRoute(
    onBack: () -> Unit,
    modifier: Modifier = Modifier,
    requireAcceptance: Boolean = false,
    onAccept: () -> Unit = {},
) {
    var declined by remember { mutableStateOf(false) }
    Column(modifier = modifier.fillMaxSize().background(AppTheme.colors.background)) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .background(AppTheme.colors.surface)
                .statusBarsPadding()
                .padding(horizontal = AppTheme.spacing.sm, vertical = AppTheme.spacing.xs),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            if (!requireAcceptance) {
                AppIconButton(
                    iconName = "arrow_back",
                    contentDescription = stringResource(Res.string.terms_back_a11y),
                    onClick = onBack,
                )
            }
            Text(
                text = stringResource(Res.string.terms_title),
                style = AppTheme.typography.headlineMd,
                color = AppTheme.colors.onSurface,
                modifier = Modifier
                    .weight(1f)
                    .padding(horizontal = AppTheme.spacing.sm),
            )
        }
        Column(
            modifier = Modifier
                .weight(1f)
                .appContentWidth(AppTheme.sizes.readingMaxWidth)
                .verticalScroll(rememberScrollState())
                .padding(AppTheme.spacing.lg),
            verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.lg),
        ) {
            // Plain-language summary card — makes a legal wall approachable and scannable before the
            // formal Terms. Explicitly flagged (below) as an overview, not the operative agreement.
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(AppTheme.colors.categoryChip, AppTheme.shapes.card)
                    .padding(AppTheme.spacing.lg),
                verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
            ) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
                ) {
                    AppIcon(name = "info", tint = AppTheme.colors.primary)
                    Text(
                        text = stringResource(Res.string.terms_summary_title),
                        style = AppTheme.typography.bodyLg,
                        color = AppTheme.colors.primary,
                    )
                }
                stringResource(Res.string.terms_summary_body).split("\n").forEach { point ->
                    Row(horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm)) {
                        Text("•", style = AppTheme.typography.bodyMd, color = AppTheme.colors.primary)
                        Text(
                            text = point,
                            style = AppTheme.typography.bodyMd,
                            color = AppTheme.colors.onSurface,
                        )
                    }
                }
                Text(
                    text = stringResource(Res.string.terms_summary_disclaimer),
                    style = AppTheme.typography.bodySm,
                    color = AppTheme.colors.onSurfaceVariant,
                )
            }

            Text(
                text = stringResource(Res.string.terms_intro),
                style = AppTheme.typography.bodyMd,
                color = AppTheme.colors.onSurfaceVariant,
            )
            // Render each numbered clause with its "N. Heading." lead-in emphasized, so the full Terms
            // read as scannable sections rather than one undifferentiated block.
            val headingStyle = SpanStyle(
                fontWeight = FontWeight.SemiBold,
                color = AppTheme.colors.onSurface,
            )
            stringResource(Res.string.terms_body).split("\n\n").forEach { section ->
                Text(
                    text = buildAnnotatedString {
                        val firstDot = section.indexOf(". ")
                        val secondDot = if (firstDot >= 0) section.indexOf(". ", firstDot + 2) else -1
                        val headingEnd = if (secondDot >= 0) secondDot + 1 else section.length
                        withStyle(headingStyle) { append(section.substring(0, headingEnd)) }
                        append(section.substring(headingEnd))
                    },
                    style = AppTheme.typography.bodyMd,
                    color = AppTheme.colors.onSurfaceVariant,
                )
            }
            Text(
                text = stringResource(Res.string.terms_updated),
                style = AppTheme.typography.labelSm,
                color = AppTheme.colors.onSurfaceVariant,
            )
        }
        if (requireAcceptance) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(AppTheme.colors.surface)
                    .padding(AppTheme.spacing.lg)
                    .navigationBarsPadding(),
                verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
            ) {
                if (declined) {
                    Text(
                        text = stringResource(Res.string.terms_decline_notice),
                        style = AppTheme.typography.bodySm,
                        color = AppTheme.colors.alert,
                    )
                }
                Text(
                    text = stringResource(Res.string.terms_consent_hint),
                    style = AppTheme.typography.bodySm,
                    color = AppTheme.colors.onSurfaceVariant,
                )
                Row(horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md)) {
                    AppButton(
                        text = stringResource(Res.string.terms_decline),
                        onClick = { declined = true },
                        variant = AppButtonVariant.Secondary,
                        modifier = Modifier.weight(1f),
                    )
                    AppButton(
                        text = stringResource(Res.string.terms_accept),
                        onClick = onAccept,
                        modifier = Modifier.weight(1f),
                    )
                }
            }
        }
    }
}
